import { Bell, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../context/DealerProfile";

const StickyBell = ({ tasks }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [refreshToggle, setRefreshToggle] = useState(false);
  const dealerDetail = useUserContext();
  const navigate = useNavigate();

  if (dealerDetail?.User_Status === "Inactive") {
    return null; // 🚫 Hide bell completely
  }


  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const markTaskAsSeen = (taskId) => {
    let seen = JSON.parse(localStorage.getItem("seenDueTasks") || "[]");
    if (!seen.includes(taskId)) {
      seen.push(taskId);
      localStorage.setItem("seenDueTasks", JSON.stringify(seen));
    }
  };

  const seen = JSON.parse(localStorage.getItem("seenDueTasks") || "[]");

  // 🧹 Remove seen IDs that are no longer valid
  const validTaskIds = new Set(tasks?.map((task) => task?.id));
  const cleanedSeen = seen.filter((id) => validTaskIds.has(id));
  localStorage.setItem("seenDueTasks", JSON.stringify(cleanedSeen));


  const dueTasks =
    tasks?.filter((task) => {
      if (!task || typeof task !== "object") return false;

      const { id, Due_Date_Time, Status, Subject } = task;

      if (!id || !Due_Date_Time || !Subject) return false;

      const status = (Status || "").toLowerCase();
      if (status === "completed") return false;

      const dueDate = new Date(Due_Date_Time);

      if (isNaN(dueDate)) return false;

      // ✅ Use cleanedSeen here
      if (cleanedSeen.includes(id)) return false;

      return true;
    }) || [];


const sortedDueTasks = [...dueTasks].sort((a, b) => {
  const dateA = new Date(a.Due_Date_Time);
  const dateB = new Date(b.Due_Date_Time);
  return dateA - dateB; // Ascending: soonest first
});



  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Bell Icon */}
      <button
        className="relative w-12 h-12 rounded-full bg-[#EBECEE] flex items-center justify-center shadow-md"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <Bell className="w-6 h-6 text-[#525252]" />
        {dueTasks.length > 0 && (
          <span className="absolute top-[4px] right-[4px] bg-red-600 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full shadow">
            {dueTasks.length}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute bottom-16 right-0 w-[340px] max-h-[420px] overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-xl p-5 z-40 animate-fadeIn">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-gray-900 flex items-center">
              <span className="mr-2" style={{ color: "#2070B9" }}>
                📅
              </span>
              {dueTasks.length > 0
                ? `You have ${dueTasks.length} task${dueTasks.length > 1 ? "s" : ""} due`
                : "All Tasks"}
            </h3>
            <button
              onClick={() => setShowDropdown(false)}
              aria-label="Close"
              className="p-1 rounded-full hover:bg-[#F0F6FA] focus:outline-none focus:ring-2"
              style={{
                color: "#2070B9",
                borderColor: "#2070B9",
                boxShadow: "0 0 0 2px #2070B933",
              }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {dueTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32">
              <span className="text-2xl mb-2" style={{ color: "#2070B9" }}>
                🎉
              </span>
              <p className="text-sm text-gray-500 text-center">
                No tasks due today. Enjoy your free time!
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {sortedDueTasks.map((task) => {
                const dueDateTime =
                  task.Due_Date_Time && !isNaN(new Date(task.Due_Date_Time))
                    ? new Date(task.Due_Date_Time).toLocaleString(undefined, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })
                    : null;

                return (
                  <li
                    key={task.id}
                    onClick={() => {
                      markTaskAsSeen(task.id);
                      setShowDropdown(false);
                      setRefreshToggle(!refreshToggle);
                      navigate(`/task-details/${task.id}`);
                    }}
                    tabIndex={0}
                    role="button"
                    className="transition-shadow cursor-pointer border border-gray-100 rounded-lg p-4 hover:bg-[#F0F6FA] focus:bg-[#E1EEFA] focus:outline-none shadow-sm"
                    style={{
                      borderLeft: `4px solid ${dueDateTime ? "#2070B9" : "#F87171"
                        }`,
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        markTaskAsSeen(task.id);
                        setShowDropdown(false);
                        setRefreshToggle(!refreshToggle);
                        navigate(`/task-details/${task.id}`);
                      }
                    }}
                  >
                    <div className="flex flex-col gap-0.5">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {task.Subject || "No Subject"}
                      </p>
                      <p
                        className={`text-xs ${dueDateTime ? "text-gray-500" : "text-red-500 font-medium"
                          }`}
                      >
                        {dueDateTime ? (
                          <>
                            <span style={{ color: "#2070B9", fontWeight: 600 }}>
                              Due:
                            </span>{" "}
                            {dueDateTime}
                          </>
                        ) : (
                          <span>⚠ Invalid due date</span>
                        )}

                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default StickyBell;
